import { Wrench, Layout, BarChart3, Target, Zap, Activity, Users, TrendingUp } from 'lucide-react';
import KeywordLandingPage, {
  type ProblemCard,
  type FeatureCard,
} from '../components/KeywordLandingPage';

const problemCards: ProblemCard[] = [
  {
    title: 'Findings are spread across tools',
    description:
      'Your SWOT, market assessment, objectives, and project list can be difficult to bring into one planning discussion.',
    icon: Wrench,
    iconBgClass: 'bg-red-100',
    iconColorClass: 'text-red-600',
  },
  {
    title: 'The blank page creates friction',
    description:
      'Knowing which framework to use still leaves the work of structuring and recording your thinking.',
    icon: Layout,
    iconBgClass: 'bg-orange-100',
    iconColorClass: 'text-orange-600',
  },
  {
    title: 'The handoff loses context',
    description:
      'When the plan and task list are separate, the strategic reason for the work becomes harder to follow.',
    icon: BarChart3,
    iconBgClass: 'bg-yellow-100',
    iconColorClass: 'text-yellow-600',
  },
];

const features: FeatureCard[] = [
  {
    title: 'Business assessment frameworks',
    description: 'Save PESTEL, Five Forces, driving forces, SWOT, and success factors.',
    icon: Layout,
    gradientFrom: 'from-blue-50',
    gradientTo: 'to-blue-100',
    iconColorClass: 'text-blue-600',
  },
  {
    title: 'Foundation builders',
    description: 'Give vision, mission, values, and measurable objectives a clear structure.',
    icon: Target,
    gradientFrom: 'from-purple-50',
    gradientTo: 'to-purple-100',
    iconColorClass: 'text-purple-600',
  },
  {
    title: 'AI starting points',
    description:
      'Draft foundation statements, strategies, projects, and tasks from the context you supply.',
    icon: Zap,
    gradientFrom: 'from-green-50',
    gradientTo: 'to-green-100',
    iconColorClass: 'text-green-600',
  },
  {
    title: 'Connected planning and execution',
    description:
      'Follow objectives through strategies to the projects and tasks that support them.',
    icon: Activity,
    gradientFrom: 'from-orange-50',
    gradientTo: 'to-orange-100',
    iconColorClass: 'text-orange-600',
  },
  {
    title: 'Organisation and ownership',
    description: 'Build an org chart and assign responsibility for the work.',
    icon: Users,
    gradientFrom: 'from-red-50',
    gradientTo: 'to-red-100',
    iconColorClass: 'text-red-600',
  },
  {
    title: 'Visual plans and reports',
    description: 'Explore your work in different views and export custom strategy reports as PDFs.',
    icon: TrendingUp,
    gradientFrom: 'from-indigo-50',
    gradientTo: 'to-indigo-100',
    iconColorClass: 'text-indigo-600',
  },
];

export default function StrategicPlanningToolsPage() {
  return (
    <KeywordLandingPage
      headline="Bring your planning tools into one clear workflow."
      subheadline="Work through your company foundation, external and internal assessments, and strategic priorities. Then connect the plan to projects and tasks in the same workspace."
      bullets={[
        'Guided vision, mission, and values builders',
        'Saved PESTEL, Five Forces, and SWOT',
        'AI drafting from your business context',
        'Objectives connected to projects and tasks',
      ]}
      badgeText="Strategic Planning Tools"
      problemTitle="Each planning exercise should move the plan forward."
      problemSubtitle="Frameworks are more useful when you can return to their findings while choosing priorities and organising the next steps."
      problemCards={problemCards}
      solutionTitle="Work through the plan with the right structure at each step."
      solutionSubtitle="StratPlan combines guided builders, saved frameworks, AI drafting, and connected execution to carry your planning work into action."
      solutionSteps={[
        {
          title: 'Build the foundation',
          description:
            'Articulate your vision, mission, and values, then define measurable objectives for the business.',
        },
        {
          title: 'Explore the business context',
          description:
            'Record external and internal assessments, including PESTEL, Five Forces, SWOT, and competitor profiles.',
        },
        {
          title: 'Turn priorities into work',
          description:
            'Develop strategies and link them to projects and tasks with owners, deadlines, and progress.',
        },
      ]}
      featuresTitle="Tools for each stage of your strategic plan"
      features={features}
      finalCtaTitle="Put your planning tools to work together."
      finalCtaSubtitle="Create your account and start building your strategic plan through one guided workflow."
      finalCtaButtonText="Start my strategic plan"
      analyticsCategory="strategic_planning_tools_landing"
      breadcrumbLabel="Strategic Planning Tools"
    />
  );
}
